import type { JobDetails as _JobDetails } from '@seek/ca-graphql-schema/types';
import {
  type AnalyticsEventType,
  type JobDetailsView,
  useJobDetailsProps as _useJobDetailsProps,
} from '@seek/job-details-view';
import type { SolMetadata } from '@seek/sol-js';
import { type ComponentProps, useContext } from 'react';

import AnalyticsContext from 'src/analytics/AnalyticsContext';
import { LinkPosition } from 'src/analytics/adapter';
import type { RecommendedJobsAnalytics } from 'src/analytics/adapter/recommendedJobs';
import { useAppConfig } from 'src/components/App/useAppConfig';
import careerInsight from 'src/components/CareerInsight/CareerInsight';
import { useSolHashByJobId } from 'src/context/solContext/solContext';
import type { ApplicantCount, JobDetails } from 'src/graphql/graphql';
import { useNumberOfApplicants } from 'src/hooks/jobDetails/useNumberOfApplicants';

import useReportJob from '../useReportJob';
import useSaveJob from '../useSaveJob';

import { useApplyTracking } from './useApplyTracking';

type JobDetailProps = ComponentProps<typeof JobDetailsView>;

interface UseJobDetailsProps {
  jobDetails: JobDetails;
  isSaved: boolean;
  isSalaryMatch: boolean;
  isAddSalaryPrefNudgeShown: boolean;
  solMetadata?: SolMetadata;
}

const useJobDetailsProps = ({
  jobDetails,
  isSaved,
  isSalaryMatch,
  isAddSalaryPrefNudgeShown,
  solMetadata,
}: UseJobDetailsProps): JobDetailProps => {
  const {
    companyProfileClicked,
    companyReviewsClicked,
    contactHirerInteracted,
    jobApplied,
    jobByThisCompanyClicked,
    jobDetailsTitleClicked,
    jobReported,
    jobShared,
    salaryBadgeClicked,
    sourcrImpression,
    sourcrClicked,
    topApplicantBadgeClicked,
    companyProfileWidgetImpression,
    companyProfileWidgetAccordionLinkClicked,
    companyProfileWidgetLinkClicked,
    companyProfileWidgetReviewsLinkClicked,
  } = useContext(AnalyticsContext) as RecommendedJobsAnalytics;

  const { zoneFeatures } = useAppConfig();
  const { SHOULD_OPEN_APPLY_IN_NEW_TAB } = zoneFeatures;
  const jobId = jobDetails.job.id;
  const { toggleSavedJob } = useSaveJob({
    id: jobId,
    isSaved,
    linkPosition: LinkPosition.JobDetails,
    solMetadata,
  });

  const solMetadataHash = useSolHashByJobId(jobId);

  const [applyUrl, onApplyClick] = useApplyTracking({
    job: jobDetails.job,
    searchRequestToken: solMetadataHash,
    isSalaryMatch,
    isAddSalaryPrefNudgeShown,
  });

  const { reportJob } = useReportJob();
  const onReportJobAd = (formValues: {
    comment: string;
    email: string;
    reason: string;
  }) => {
    jobReported();
    return reportJob({
      jobId: Number(jobId),
      ...formValues,
    });
  };

  const onAnalyticsEvent = (event: AnalyticsEventType) => {
    switch (event.type) {
      case 'JOB_TITLE_LINK_CLICK':
        jobDetailsTitleClicked();
        break;
      case 'SHARE_LINK_CLICK':
        jobShared({ jobShareType: event.channel });
        break;
      case 'COMPANY_PROFILE_LINK_CLICK':
        companyProfileClicked();
        break;
      case 'COMPANY_REVIEWS_LINK_CLICK':
        companyReviewsClicked();
        break;
      case 'COMPANY_JOB_SEARCH_LINK_CLICK':
        jobByThisCompanyClicked();
        break;
      case 'CONTACT_MATCH_INTERACTION':
        contactHirerInteracted({
          jobContactType: event.contactType,
          jobContactAction: event.eventType,
        });
        break;
      case 'TOP_APPLICANT_BADGE_CLICK': {
        topApplicantBadgeClicked();
        break;
      }
      case 'SALARY_BADGE_CLICKED': {
        salaryBadgeClicked();
        break;
      }
      case 'SOURCR_IMPRESSION': {
        sourcrImpression();
        return;
      }
      case 'SOURCR_CLICKED': {
        sourcrClicked();
        return;
      }
      case 'COMPANY_PROFILE_WIDGET_IMPRESSION': {
        companyProfileWidgetImpression();
        return;
      }
      case 'COMPANY_PROFILE_WIDGET_ACCORDION_LINK_CLICK': {
        companyProfileWidgetAccordionLinkClicked({
          linkAction: event.linkAction,
        });
        return;
      }
      case 'COMPANY_PROFILE_WIDGET_LINK_CLICK': {
        companyProfileWidgetLinkClicked({
          linkPosition:
            event.linkPosition === 'below' ? LinkPosition.Below : undefined,
        });
        return;
      }
      case 'COMPANY_PROFILE_WIDGET_REVIEWS_LINK_CLICK': {
        companyProfileWidgetReviewsLinkClicked({
          linkPosition:
            event.linkPosition === 'below' ? LinkPosition.Below : undefined,
        });
        return;
      }
    }
  };

  const ctas = {
    onApplyClick: () => {
      jobApplied('Recommended Jobs (Splitview)');
      return onApplyClick();
    },
    onReportJobAd,
    onToggleSaveJob: () => toggleSavedJob(),
    trackVerifiedHirerBadgeClick: () => {},
    trackMoreAboutThisCompanyClick: () => {},
  };

  const { job } = _useJobDetailsProps({
    jobDetails: jobDetails as _JobDetails,
    jobApplyUrl: applyUrl,
    flags: {
      appliedDateFormatted:
        jobDetails?.personalised?.appliedDateTime?.longAbsoluteLabel,
      isSaved,
      isAuthenticated: true,
    },
    topApplicant: jobDetails?.personalised?.topApplicantBadge,
    salaryMatch: jobDetails?.personalised?.salaryMatch,
  });

  const { applicationVolumeLabel } = useNumberOfApplicants(
    (jobDetails.insights?.[0] as ApplicantCount) ?? {
      countLabel: '',
      volumeLabel: '',
    },
  );

  return {
    job,
    ctas,
    onAnalyticsEvent,
    careerInsights:
      (jobDetails?.learningInsights?.content &&
        careerInsight({
          content: jobDetails?.learningInsights?.content,
          lmisSnippet: jobDetails?.learningInsights?.analytics,
        })) ||
      undefined,
    uniqueContainerValue: 'job-details-split-view',
    shouldOpenApplyInNewTab: SHOULD_OPEN_APPLY_IN_NEW_TAB,
    displayCompanyProfile: true,
    embedded: true,
    renderRelativePaths: true,
    applicationVolumeLabel,
    jobUrlOpts: {
      params: {
        type: jobDetails.job.tracking?.adProductType || '',
        ref: 'recom-standalone',
      },
      meta: {
        solMetadataReference: solMetadataHash,
      },
    },
  };
};

export default useJobDetailsProps;
